import React, { useLayoutEffect, useRef } from 'react';
import Footer from '../../Footer';
import './Home.scss';
import { motion, useMotionValueEvent, useScroll, useSpring, useTransform } from 'framer-motion';
import { useRandom, useWindowSize } from '../../../helpers/functions/hooks';
import RenderIf from '../../RenderIf';
import { occupations } from '../../../helpers/constants';
import Spacer from '../../Spacer';

const Home = ({ homeSection }: { homeSection: React.RefObject<HTMLElement> }): JSX.Element => {
    const mainTextContainer = useRef<HTMLDivElement>(null);
    const homeScrollYProgress = useScroll({
        target: homeSection,
        offset: ["end end", "end start"]
    }).scrollYProgress;

    useMotionValueEvent(homeScrollYProgress, "change", (latest) => {
        if (latest > 0.5) {
            mainTextContainer.current?.classList.add('ignore-clicks');
        }
        else {
            mainTextContainer.current?.classList.remove('ignore-clicks');
        }
    });

    const springOptions = { stiffness: 150, damping: 18 };

    const { width } = useWindowSize();
    const chosenOccupation = useRandom(occupations) + '.';
    const opacityAnim = useTransform(homeScrollYProgress, (value) => 1 - value * 2);
    let rightAnim = useSpring(useTransform(homeScrollYProgress, [0, 0.5, 0.51], [0, 140, 140], { clamp: false }), springOptions);
    let leftAnim = useSpring(useTransform(homeScrollYProgress, [0, 0.5, 0.51], [0, -110, -110], { clamp: false }), springOptions);
    let downAnim = useTransform(homeScrollYProgress, [0, 0.1], [0, 40], { clamp: false });

    const topText = useRef<HTMLHeadingElement>(null);
    const nameText = useRef<SVGTextElement>(null);
    const nameTextLast = useRef<SVGTextElement>(null);
    const bottomText = useRef<HTMLHeadingElement>(null);

    const isMobile = useRef<boolean>(width <= 500);
    const isSmallMobile = useRef<boolean>(width <= 360);
    const isFirstPaint = useRef<boolean>(true);

    useLayoutEffect(() => {
        if (isMobile.current === (width <= 500) && isSmallMobile.current === (width <= 360) && !isFirstPaint.current) {
            return;
        }
        isMobile.current = width <= 500;
        isSmallMobile.current = width <= 360;
        isFirstPaint.current = false;

        const topTextHTML = topText.current!;
        const nameTextHTML = nameText.current!
        const nameTextLastHTML = nameTextLast.current!;
        const bottomTextHTML = bottomText.current!;

        topTextHTML.innerHTML = topTextHTML.textContent!.replace(/\S/g, "<span class='letter'>$&</span>");
        const name = width <= 500 ? 'Pranav' : 'Pranav Pakanati';
        nameTextHTML.innerHTML = name.replace(/\S/g, "<tspan class='letter'>$&</tspan>");
        if (nameTextLastHTML != null) {
            nameTextLastHTML.innerHTML = (nameTextLastHTML.textContent ?? '').replace(/\S/g, "<tspan class='letter'>$&</tspan>") ?? '';
        }
        bottomTextHTML.innerText = 'I\xa0am\xa0a... ';
        bottomTextHTML.innerHTML = bottomTextHTML.textContent!.replace(/\S/g, "<span class='letter'>$&</span>");

        const occupation = chosenOccupation.replace(/\S/g, "<span class='letter'>$&</span>");
        bottomTextHTML.innerHTML = bottomTextHTML.innerHTML + `${width <= 360 ? '<br />' : ''}<span id='occupation'>` + occupation + "</span>";
        const topLetters = document.querySelectorAll<HTMLElement>('#hi-my-name-is .letter');
        topLetters.forEach((e, i) => {
            e.style.animationDelay = `${1000 + 500 * (i / topLetters.length)}ms`;
        });

        const nameLetters = document.querySelectorAll<HTMLElement>('#main-name-text .letter');
        nameLetters.forEach((e, i) => {
            const delay = 1000 + 500 * (i / nameLetters.length);
            e.style.animationDelay = `${delay}ms`;
            e.innerHTML = e.textContent + `<animate attributeName="dy" values="15; 0" dur="1000ms" begin="${delay}ms" fill="freeze" keyTimes="0; 1" keySplines="0.16, 1, 0.3, 1" calcMode="spline" />`
        });

        const lastNameLetters = document.querySelectorAll<HTMLElement>('#last-name-text .letter');
        lastNameLetters.forEach((e, i) => {
            const delay = 1000 + 500 * (i / lastNameLetters.length);
            e.style.animationDelay = `${delay}ms`;
            e.innerHTML = e.textContent + `<animate attributeName="dy" values="15; 0" dur="1000ms" begin="${delay}ms" fill="freeze" keyTimes="0; 1" keySplines="0.16, 1, 0.3, 1" calcMode="spline" />`
        });

        const bottomLetters = document.querySelectorAll<HTMLElement>('#i-am .letter');
        bottomLetters.forEach((e, i) => {
            e.style.animationDelay = `${1000 + 500 * (i / bottomLetters.length)}ms`;
        });
    }, [width, chosenOccupation]);

    return (
        <section id="home" ref={homeSection}>
            <div id="main-text-container" ref={mainTextContainer}>
                <motion.h2 style={{ x: width <= 500 ? 0 : leftAnim, opacity: opacityAnim }} id="hi-my-name-is" ref={topText}>hi, my name is</motion.h2>
                <motion.svg style={{ x: width <= 500 ? 0 : rightAnim, opacity: opacityAnim }} id="name-title" width="100%" height={`calc(${width <= 500 ? 'calc(2 * clamp(3rem, 16vw - 3rem, 4rem))' : 'clamp(3rem, 16vw - 3rem, 4rem)'} + calc(2 * clamp(1rem, 8vw - 2rem, 1.5rem)))`}>
                    <linearGradient id="gr-simple" x1="0" y1="50%" x2="2" y2="50%" spreadMethod="repeat">
                        <animate attributeName="x1" dur="3s" from="0" to="-2" begin="2s" repeatCount="indefinite" />
                        <animate attributeName="x2" dur="3s" from="2" to="0" begin="2s" repeatCount="indefinite" />
                        <stop stop-color="#2F13CD" offset="0%"></stop>
                        <stop stop-color="#FF0A99" offset="50%"></stop>
                        <stop stop-color="#2F13CD" offset="100%"></stop>
                    </linearGradient>
                    <text textAnchor='middle' dominantBaseline={width <= 500 ? undefined : 'central'} x="50%" y={width <= 500 ? "42%" : "50%"} ref={nameText} id='main-name-text'>Pranav <tspan>{width > 500 ? 'Pakanati' : ''}</tspan></text>
                    <RenderIf condition={width <= 500} fallback={<div></div>}>
                        <text textAnchor='middle' dominantBaseline={width <= 500 ? undefined : 'central'} x="50%" y={width <= 500 ? "86%" : "50%"} ref={nameTextLast} id='last-name-text'>Pakanati</text>
                    </RenderIf>
                </motion.svg>
                <motion.h2 style={{ x: width <= 500 ? 0 : leftAnim, opacity: opacityAnim }} id="i-am" ref={bottomText}>I&nbsp;am&nbsp;a... <span id="occupation">web developer.</span></motion.h2>
                <motion.a style={{ opacity: opacityAnim }} href="#about-me" className="hover-link" onClick={e => {
                        e.preventDefault();
                        document.getElementById('about-me')?.scrollIntoView({ behavior: 'smooth' });
                    }}>
                    <div id="scroll-for-more-container">
                        <p>scroll for more</p>
                        <img src="/arrow_downward.svg" alt="downwards arrow"></img>
                    </div>
                </motion.a>
            </div>
            <motion.div style={{ y: downAnim }} id="footer">
                <Spacer />
                <Footer />
            </motion.div>
        </section>
    )
}

export default Home;